// =====================================================================
// Cart server API — sync cart with users.cart_draft (cloud backup).
// Uses the `cart-sync` Edge Function (auth via x-tma-init-data).
// Falls back silently on network/initData failure.
// =====================================================================

import { EDGE_URLS } from "@/shared/api/edge-urls";
import { CartItemSchema, type CartItem } from "@/shared/lib/db-schema";
import { parseRows } from "@/shared/lib/parse";

function isDev(): boolean {
  return import.meta.env.DEV === true;
}

interface CartDraftServer {
  _v: number;
  items: unknown[];
}

interface CartSyncGetResponse {
  ok: boolean;
  items?: CartDraftServer;
  code?: string;
  message?: string;
}

interface CartSyncSetResponse {
  ok: boolean;
  code?: string;
  message?: string;
}

const EMPTY: CartItem[] = [];

export async function fetchCartFromServer(initData: string): Promise<ReadonlyArray<CartItem>> {
  if (initData.length === 0) return EMPTY;
  try {
    const res = await fetch(EDGE_URLS.cartSync, {
      headers: { "x-tma-init-data": initData },
    });
    if (!res.ok) return EMPTY;
    const raw: unknown = await res.json();
    if (!raw || typeof raw !== "object") return EMPTY;
    const body = raw as CartSyncGetResponse;
    if (body.ok !== true) return EMPTY;
    const draft = body.items;
    if (!draft || typeof draft !== "object") return EMPTY;
    if (!Array.isArray(draft.items)) return EMPTY;
    return parseRows(CartItemSchema, draft.items, "CartItem");
  } catch (e) {
    if (isDev() && e instanceof Error) console.warn("[cart] fetchCartFromServer:", e.message);
    return EMPTY;
  }
}

export async function saveCartToServer(initData: string, items: ReadonlyArray<CartItem>): Promise<boolean> {
  if (initData.length === 0) return false;
  try {
    const res = await fetch(EDGE_URLS.cartSync, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-tma-init-data": initData,
      },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) return false;
    const raw: unknown = await res.json();
    if (!raw || typeof raw !== "object") return false;
    const body = raw as CartSyncSetResponse;
    return body.ok === true;
  } catch (e) {
    if (isDev() && e instanceof Error) console.warn("[cart] saveCartToServer:", e.message);
    return false;
  }
}
